// PostHog must initialize before anything can capture (Sentry, router, auth).
import './lib/posthog'
// Bridges TanStack's onlineManager to @capacitor/network (drives the offline
// screen) — must run before any query can fire.
import './lib/network'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc)
dayjs.extend(timezone)
import { ConvexProviderWithAuth } from '@pronteer/convex/react'
import { ConvexReactClient } from '@pronteer/convex'
import { RouterProvider } from '@tanstack/react-router'
import { useSupabaseAuth } from './hooks/useSupabaseAuth'
import { router } from './router'
import { ThemeProvider } from './components/theme-provider'
import { BiometricLockProvider } from './components/BiometricLockProvider'
import { OfflineProvider } from './components/OfflineProvider'
import { getActiveCompanyId } from './lib/company-context'
import { initKeyboard } from './lib/keyboard'
import { registerAuthDeepLinkHandler } from './lib/deep-links'
import { enforceFreshSessionOnUpdate } from './lib/app-version'
import { forceUpdateStore, isClientOutdatedError } from './lib/force-update'
import { UpdateRequiredScreen } from './components/UpdateRequiredScreen'
import { publicOrpcClient } from './lib/orpc'
import { getFaceIdEnabled } from './lib/face-id'
import { getPasscodeEnabled } from './lib/passcode'
import * as Sentry from '@sentry/capacitor'
import * as SentryReact from '@sentry/react'
// PostHog wrappers (init lives in ./lib/posthog).
import { trackScreen, identifyUser, resetUser } from './utils/analytics'
import { supabase } from './lib/supabase'

import './index.css'

Sentry.init(
  {
    dsn: 'https://a1e31dbc55a47cc6a57d10d4af572f45@o4511132879224832.ingest.us.sentry.io/4511132880470016',
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/capacitor/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    // Set your release version, such as "getsentry@1.0.0"
    release: 'my-project-name@<release-name>',
    // Set your dist version, such as "1"
    dist: '<dist>',
    // Logs requires @sentry/capacitor 2.0.0 or newer.
    enableLogs: true,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/api\..*\.pronteer\.com/],
  },
  // Forward the init method from @sentry/react
  SentryReact.init
)

const convexUrl = import.meta.env.VITE_CONVEX_URL

if (!convexUrl) {
  throw new Error(
    'Missing Convex URL. Please set VITE_CONVEX_URL in .env.local'
  )
}

const queryClient = new QueryClient({
  // Any request can be the one that trips the force-update gate, so detect the
  // API's CLIENT_OUTDATED code globally on both queries and mutations.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isClientOutdatedError(error)) {
        forceUpdateStore.trigger((error as { data?: { minVersion?: string; clientVersion?: string } }).data)
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      // The client is too old for the API — show the blocking update gate and
      // suppress the generic error toast.
      if (isClientOutdatedError(error)) {
        forceUpdateStore.trigger((error as { data?: { minVersion?: string; clientVersion?: string } }).data)
        return
      }
      // Surface failures so a hung/failed fetch shows a toast instead of an
      // infinite skeleton. Only when there's nothing to show — avoid noise on
      // background-refetch failures where stale data is still on screen, and
      // skip the auth-retry path (401s).
      // Queries that surface their own errors inline (e.g. device location)
      // opt out via meta.suppressGlobalErrorToast.
      if (query.meta?.suppressGlobalErrorToast) return
      if (query.state.data === undefined && (error as any)?.status !== 401) {
        toast.error(
          (error as any)?.name === 'TimeoutError'
            ? 'Request timed out. Please try again.'
            : 'Something went wrong loading data.'
        )
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      // Both off so a resume or reconnect can never refetch under an open form
      // and discard in-progress edits: getById responses carry Dates, which
      // defeats structural sharing, so every refetch hands drawers a new object
      // identity and re-fires their reset() effect. Refresh happens on
      // navigation via refetchOnMount.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        // Retry 401s up to 3 times (auth may not be ready yet on initial load)
        if (error && 'status' in error && (error as any).status === 401) {
          return failureCount < 3
        }
        return failureCount < 1
      },
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 3000),
    },
    mutations: {
      // 'always' so a mutation fired as the connection drops fails right away
      // instead of being paused and replayed later with stale form values.
      networkMode: 'always',
      retry: false,
    },
  },
})

const convex = new ConvexReactClient(convexUrl)

// Track screen views on route changes
router.subscribe('onResolved', ({ toLocation }) => {
  console.debug('Navigated to', toLocation.pathname)
  trackScreen(toLocation.pathname)
})

// Associate PostHog with the signed-in user — fires on fresh sign-in and on
// session restore at launch; reset unlinks the device on sign-out.
supabase.auth.onAuthStateChange((event, session) => {
  if (
    session?.user &&
    (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'USER_UPDATED')
  ) {
    identifyUser(session.user.id, {
      email: session.user.email,
      name: session.user.user_metadata?.full_name,
    })
  } else if (event === 'SIGNED_OUT') {
    resetUser()
    // Covers every sign-out path (including token revocation and the
    // fresh-session guard), not just useAuth().signOut.
    queryClient.clear()
  }
})

// Check for a force-update as soon as the app starts — independent of sign-in.
// Fire-and-forget so it never blocks first paint; if the client is too old the
// store flips and <UpdateRequiredScreen/> appears as soon as the response lands.
// Other failures (offline, etc.) are ignored — the per-request gate still covers
// the signed-in app.
function checkAppVersionAtStartup() {
  void publicOrpcClient.version.check().catch((error: unknown) => {
    if (isClientOutdatedError(error)) {
      forceUpdateStore.trigger(
        (error as { data?: { minVersion?: string; clientVersion?: string } }).data
      )
    }
  })
}

async function bootstrap() {
  // Kick off the version check immediately; do not await it.
  checkAppVersionAtStartup()

  // Must run before Supabase reads the persisted session — clears local auth
  // state when the app is freshly installed or updated to a new build.
  await enforceFreshSessionOnUpdate()
  await getActiveCompanyId()
  await Promise.all([getFaceIdEnabled(), getPasscodeEnabled()])
  registerAuthDeepLinkHandler()

  // Mark the native build so CSS can scope web-only desktop styling (e.g. the
  // full-width layout) without affecting the native app. Set before first paint
  // so there's no post-mount class flip / reflow.
  if (Capacitor.isNativePlatform()) {
    document.documentElement.classList.add('native')
  }

  // Keyboard.resize is "none" — publish the keyboard height (--keyboard-height
  // + useKeyboardHeight) so bottom sheets and composers lift above it.
  initKeyboard()

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConvexProviderWithAuth client={convex} useAuth={useSupabaseAuth}>
          <ThemeProvider defaultTheme="light" storageKey="pronteer-ui-theme">
            <OfflineProvider>
              <BiometricLockProvider>
                <RouterProvider router={router} />
                {/* Blocking force-update gate — renders above everything when
                    the API reports the client is too old (CLIENT_OUTDATED). */}
                <UpdateRequiredScreen />
              </BiometricLockProvider>
            </OfflineProvider>
          </ThemeProvider>
        </ConvexProviderWithAuth>
      </QueryClientProvider>
    </StrictMode>
  )
}

bootstrap()
